import { alpha, type Theme } from '@mui/material/styles';

const INSET_X = 16; // matches the rows' px: 2

const solidLayer = (color: string) => `linear-gradient(${color}, ${color})`;

const getStripeColor = (theme: Theme) => theme.palette.action.hover;

/** Translucent hover tint for grid rows — a light version of the secondary accent. */
export const getGridRowHoverColor = (theme: Theme) => alpha(theme.palette.secondary.main, 0.12);

/**
 * Opaque row background (so the skeleton behind doesn't show through) with a
 * stripe on odd rows, layered as background images. With `hover`, the hover
 * tint is stacked on top; omit it when a child (e.g. ListItemButton) supplies
 * its own translucent hover.
 */
export const gridRowBackground = (theme: Theme, index: number, { hover = true } = {}) => {
    const stripe = index % 2 === 1 ? solidLayer(getStripeColor(theme)) : null;
    return {
        bgcolor: theme.palette.background.default,
        backgroundImage: stripe ?? 'none',
        ...(hover && {
            '&:hover': {
                backgroundImage: [solidLayer(getGridRowHoverColor(theme)), stripe].filter(Boolean).join(', '),
            },
        }),
    };
};

type Bar = { top: number; height: number; width: string };

const getBars = (rowHeight: number, twoLine: boolean): Bar[] => {
    if (!twoLine) {
        return [{ top: (rowHeight - 12) / 2, height: 12, width: '35%' }];
    }
    // Title + secondary line, vertically centred as a group (12 + 10 gap + 10).
    const top = (rowHeight - 32) / 2;
    return [
        { top, height: 12, width: '60%' },
        { top: top + 22, height: 10, width: '40%' },
    ];
};

/**
 * Background layers for a virtualized list container that paint empty grid
 * rows — a divider at the bottom of each row, faint placeholder bars and the
 * alternating stripe — so
 * areas the virtualizer hasn't rendered yet (e.g. during fast scrolling) show
 * a skeleton instead of blank space. The browser paints this with the scroll,
 * with no React work. Rendered rows need an opaque background to cover it.
 */
export const gridSkeletonBackground = (theme: Theme, rowHeight: number, twoLine = false) => {
    const barColor = theme.palette.action.selected;
    const layers = [
        {
            image: `linear-gradient(to top, ${theme.palette.divider} 1px, transparent 1px)`,
            size: `100% ${rowHeight}px`,
            position: '0 0',
        },
        ...getBars(rowHeight, twoLine).map(bar => ({
            image: `linear-gradient(to bottom, transparent ${bar.top}px, ${barColor} ${bar.top}px, ${barColor} ${bar.top + bar.height}px, transparent ${bar.top + bar.height}px)`,
            size: `${bar.width} ${rowHeight}px`,
            position: `${INSET_X}px 0`,
        })),
        // Alternating stripe on odd rows, matching gridRowBackground.
        {
            image: `linear-gradient(to bottom, transparent ${rowHeight}px, ${getStripeColor(theme)} ${rowHeight}px)`,
            size: `100% ${rowHeight * 2}px`,
            position: '0 0',
        },
    ];

    return {
        backgroundImage: layers.map(layer => layer.image).join(', '),
        backgroundSize: layers.map(layer => layer.size).join(', '),
        backgroundPosition: layers.map(layer => layer.position).join(', '),
        backgroundRepeat: 'repeat-y',
    };
};
